import { useMemo } from 'react';
import { PrivateMeetingSessionDetailsListResponseType, ReplaysListResponseType } from '../../../../lib/api';

export interface ISessionGroupByTrack {
  track: string;
  featuredSessions: PrivateMeetingSessionDetailsListResponseType;
  sessions: PrivateMeetingSessionDetailsListResponseType;
}

const compareTracksAlphabetically = (t1: string, t2: string): number => t1?.localeCompare(t2);

const moveEmptyTrackToEnd = (tracks: string[]): string[] => {
  const emptyTrackIndex = tracks.findIndex(track => !track);
  const hasEmptyTrack = emptyTrackIndex !== -1;

  if (!hasEmptyTrack) return tracks;

  const reordered = [...tracks];
  const [emptyTrack] = reordered.splice(emptyTrackIndex, 1);
  reordered.push(emptyTrack);
  return reordered;
};

const getSortedUniqueTracks = (sessions: ReplaysListResponseType): string[] => {
  const allTrackNames = sessions.map(meeting => meeting.track);
  const uniqueTracks = Array.from(new Set(allTrackNames));
  const sortedTracks = uniqueTracks.sort(compareTracksAlphabetically);

  return moveEmptyTrackToEnd(sortedTracks);
};

const groupSessionsByTrack = (
  sessions: ReplaysListResponseType,
  tracks: string[],
): ISessionGroupByTrack[] => {
  return tracks.map(track => {
    const sessionsInTrack = sessions.filter(meeting => meeting.track === track);
    const featuredSessions = sessionsInTrack.filter(meeting => meeting.featured);
    const regularSessions = sessionsInTrack.filter(meeting => !meeting.featured);

    return {
      track,
      featuredSessions,
      sessions: regularSessions,
    };
  });
};

export const useSessionsByTrack = (sessions: ReplaysListResponseType = []): ISessionGroupByTrack[] => {
  const tracks = useMemo(() => getSortedUniqueTracks(sessions), [sessions]);

  return useMemo(
    () => groupSessionsByTrack(sessions, tracks),
    [sessions, tracks],
  );
};
